import { useMemo } from 'react';

import { useDebounce } from '~/hooks/useDebounce';
import { PersonFilterInput, useAvailableGlobalPeopleCountQuery } from '~/types/api.graphql';
import { pulseTopicIdsToGraphQLFilter } from '~/views/audiences/NewPulseAudience/utils/graphql';

// Batches a burst of edits (topic toggles, geo filter confirms) into a single count request.
const PEOPLE_COUNT_DEBOUNCE_MS = 400;

// Debounced in place of the query key while the selection isn't known yet.
const PENDING_QUERY_KEY = '';

export interface PulseSelection {
  topicIds: string[];
  /** Person filters (e.g. geo filters) narrowing the topics. */
  filters: PersonFilterInput[];
}

interface PulsePeopleCountArgs {
  /** The selection to count; null while it isn't known yet, which shows as loading. */
  selection: PulseSelection | null;
  /** False when the surface isn't a Pulse one: no query and no count. */
  enabled: boolean;
  /** False on submitted or stale surfaces: they never query, and show a count only if it is already cached. */
  active: boolean;
}

export interface PulsePeopleCount {
  totalCount: number | null;
  loading: boolean;
  failed: boolean;
}

const NO_TOPICS_PEOPLE_COUNT: PulsePeopleCount = { totalCount: 0, loading: false, failed: false };
const PENDING_PEOPLE_COUNT: PulsePeopleCount = { totalCount: null, loading: true, failed: false };

/**
 * Count of the people a Pulse selection matches, re-queried whenever the selection changes.
 * Counted client-side with the same filters as the non-agent Pulse flow (FilterContext): the person
 * filters plus a PulseTopic filter over the topic ids, at global scope. Nothing reaches the agent.
 *
 * Returns null when disabled, or when inactive with no cached count; with no topic selected nobody
 * matches, so the count is 0.
 */
export const usePulsePeopleCount = ({ selection, enabled, active }: PulsePeopleCountArgs): PulsePeopleCount | null => {
  // Callers hand over a fresh selection on every edit, so debounce a serialized key instead.
  const queryKey = useMemo(
    () => (selection ? JSON.stringify(buildPulseCountFilters(selection)) : PENDING_QUERY_KEY),
    [selection],
  );
  // Inactive surfaces never edit, so they skip the debounce and read the cache straight away.
  const debouncedQueryKey = `${useDebounce(queryKey, active ? PEOPLE_COUNT_DEBOUNCE_MS : 0)}`;
  const debouncedFilters = useMemo(
    () => (debouncedQueryKey ? (JSON.parse(debouncedQueryKey) as PersonFilterInput[]) : []),
    [debouncedQueryKey],
  );
  const isDebouncing = queryKey !== debouncedQueryKey;
  const hasTopics = !!selection?.topicIds.length;

  const { data, previousData, loading, error } = useAvailableGlobalPeopleCountQuery({
    variables: { filters: debouncedFilters },
    skip: !enabled || !hasTopics || isDebouncing,
    fetchPolicy: active ? 'cache-first' : 'cache-only',
  });

  if (!enabled) return null;
  if (!selection) return active ? PENDING_PEOPLE_COUNT : null;
  if (!hasTopics) return active ? NO_TOPICS_PEOPLE_COUNT : null;

  // `data` is undefined while a refetch is in flight; `previousData` keeps the last count available.
  const totalCount = (data ?? previousData)?.peopleSnapshot?.totalCount;
  if (!active) return totalCount == null ? null : { totalCount, loading: false, failed: false };
  return {
    totalCount: totalCount ?? null,
    // A pending debounce counts as loading, so an edit is acknowledged before the request fires.
    loading: loading || isDebouncing,
    failed: !!error,
  };
};

/**
 * The person filters plus a PulseTopic filter over the topic ids. The ids are deduplicated and
 * sorted, so the same topics in any order are the same query and Apollo reuses its result.
 */
export const buildPulseCountFilters = ({ topicIds, filters }: PulseSelection): PersonFilterInput[] => {
  const topicFilter = pulseTopicIdsToGraphQLFilter([...new Set(topicIds)].sort());
  return topicFilter ? [...filters, topicFilter] : filters;
};
